import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { formatApiError } from '@/lib/api';
import { SupportHelpLine } from '@/components/SupportHelpLine';
import { PhoneOtpField } from '@/components/PhoneOtpField';
import { Fingerprint, ArrowLeft, CheckCircle, Sparkle } from '@phosphor-icons/react';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm: '',
    company_name: '', phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [phoneVerification, setPhoneVerification] = useState(null); // { phone, token }
  // Trial length pulled live from the public marketing endpoint so copy
  // stays in sync with SuperAdmin's `default_trial_days` setting.
  const [trialDays, setTrialDays] = useState(15);
  useEffect(() => {
    let cancelled = false;
    api.get('/marketing/config')
      .then(r => { if (!cancelled) setTrialDays(r.data?.trial_days ?? 15); })
      .catch(() => { /* keep default */ });
    api.get('/otp/config').then(r => { if (!cancelled) setOtpEnabled(!!r.data?.enabled); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Enter your full name');
    if (!form.email.trim()) return setError('Enter your email');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    if (!form.company_name.trim()) return setError('Enter your company name');
    if (otpEnabled && !phoneVerification) return setError('Please verify your mobile number with OTP first');

    setLoading(true);
    try {
      await api.post('/auth/signup-trial', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        company_name: form.company_name.trim(),
        phone: form.phone.trim(),
        phone_verification_token: phoneVerification?.token || '',
      });
      // Re-hydrate auth context so the dashboard knows we're logged in.
      if (refreshAuth) await refreshAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Marketing panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A0A0A] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/20 rounded-sm rotate-12" />
          <div className="absolute bottom-20 right-20 w-48 h-48 border border-white/20 rounded-sm -rotate-6" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/20 rounded-sm rotate-45" />
        </div>
        <div className="z-10 px-12 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B82F6] via-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-lg shadow-blue-500/30 shrink-0">
              <Sparkle size={34} weight="fill" className="text-white" />
            </div>
            <div className="leading-none">
              <div className="text-[2.5rem] font-extrabold tracking-tight text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                SMSuite<span className="text-[#60A5FA]">Pro</span>
              </div>
              <div className="text-[12px] uppercase tracking-[0.22em] font-bold text-[#60A5FA] mt-2.5">Service Management Suite Pro</div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-[0.2em] text-fuchsia-200 mb-6">
            <Sparkle size={11} weight="fill" className="text-fuchsia-300" /> Trial Plan - {trialDays} days, no credit card
          </div>
          <p className="text-sm text-[#60A5FA] font-semibold tracking-wider mb-6">Manage. Track. Grow.</p>
          <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
            Spin up your own tenant in seconds. Manage invoices, customers, finances and follow-ups -
            all isolated per business, all yours.
          </p>
          <ul className="space-y-3 text-sm text-zinc-300">
            <Bullet>Full dashboard, invoices, advances & finance ledger</Bullet>
            <Bullet>Basic — <b>₹399/mo</b> <span className="line-through text-zinc-500 text-xs">₹999</span> · 1 user · unlimited invoices</Bullet>
            <Bullet>Pro — <b>₹999/mo</b> <span className="line-through text-zinc-500 text-xs">₹1,999</span> · unlimited users · unlimited invoices</Bullet>
            <Bullet>Your data lives in its own tenant — never mixed with anyone else</Bullet>
          </ul>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/login" className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-6" data-testid="back-to-login">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center">
              <Fingerprint size={22} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">SMSuite<span className="text-[#3B82F6]">Pro</span></span>
          </div>

          <h2 className="text-2xl tracking-tight font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Start your free trial</h2>
          <p className="text-sm text-[#52525B] mb-6">{trialDays} days on the Basic plan — no credit card required.</p>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg" data-testid="signup-error">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Your name" required>
                <Input value={form.name} onChange={update('name')} placeholder="Anand Kumar" required data-testid="signup-name" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" required autoComplete="email" data-testid="signup-email" />
              </Field>
            </div>

            <Field label={otpEnabled ? 'Mobile number (OTP verification)' : 'Phone'} required={otpEnabled}>
              <PhoneOtpField
                phone={form.phone}
                onPhoneChange={update('phone')}
                enabled={otpEnabled}
                verified={!!phoneVerification}
                onVerified={setPhoneVerification}
                onReset={() => setPhoneVerification(null)}
              />
            </Field>

            <Field label="Company name" required>
              <Input value={form.company_name} onChange={update('company_name')} placeholder="e.g. Anand Digital Services" required data-testid="signup-company" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Password" required>
                <Input type="password" value={form.password} onChange={update('password')} placeholder="Min 8 characters" required autoComplete="new-password" data-testid="signup-password" />
              </Field>
              <Field label="Confirm password" required>
                <Input type="password" value={form.confirm} onChange={update('confirm')} placeholder="Re-enter password" required autoComplete="new-password" data-testid="signup-confirm" />
              </Field>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-lg h-10 mt-2"
              data-testid="signup-submit"
            >
              {loading ? 'Creating your tenant…' : `Start ${trialDays}-day Free Trial`}
            </Button>

            <p className="text-xs text-[#52525B] text-center">
              By creating an account you agree to our terms of service.
            </p>
          </form>
          <SupportHelpLine className="mt-5 pt-4 border-t border-[#F4F4F5]" testid="signup-support-help" />
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs tracking-wide uppercase font-semibold text-[#52525B]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
